import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Cookie, X } from "lucide-react";

// =====================================================================
// COOKIE BANNER — GDPR + Italian Garante compliant (opt-in model).
// =====================================================================
// Stores consent in localStorage under "reelio_consent" as a JSON:
//   { essential: true, analytics: bool, marketing: bool, at: ISO }
// Other parts of the app can read consent via the helpers below.
// =====================================================================

const CONSENT_KEY = "reelio_consent";

export function readConsent() {
  try {
    const raw = localStorage.getItem(CONSENT_KEY);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

export function hasAnalyticsConsent() {
  return readConsent()?.analytics === true;
}

function saveConsent(partial) {
  const payload = {
    essential: true,
    analytics: false,
    marketing: false,
    ...partial,
    at: new Date().toISOString(),
    version: "1.0",
  };
  try {
    localStorage.setItem(CONSENT_KEY, JSON.stringify(payload));
  } catch {
    /* localStorage might be disabled — banner will reappear next visit */
  }
  // Inform the rest of the page (we don't reload — pieces of the app
  // can react to "reelio:consent-changed" if they need to bootstrap
  // analytics on-the-fly).
  window.dispatchEvent(new CustomEvent("reelio:consent-changed", { detail: payload }));
}

export default function CookieBanner() {
  const [show, setShow] = useState(false);
  const [customizing, setCustomizing] = useState(false);
  const [analytics, setAnalytics] = useState(false);
  const [marketing, setMarketing] = useState(false);

  useEffect(() => {
    // Defer one tick so the rest of the page paints first
    const t = setTimeout(() => setShow(!readConsent()), 200);
    return () => clearTimeout(t);
  }, []);

  if (!show) return null;

  const acceptAll = () => {
    saveConsent({ analytics: true, marketing: true });
    setShow(false);
  };
  const rejectAll = () => {
    saveConsent({ analytics: false, marketing: false });
    setShow(false);
  };
  const saveCustom = () => {
    saveConsent({ analytics, marketing });
    setShow(false);
  };

  return (
    <div
      data-testid="cookie-banner"
      role="dialog"
      aria-label="Cookie consent"
      className="fixed bottom-4 left-4 right-4 md:left-6 md:right-6 md:bottom-6 z-[100] max-w-xl md:max-w-2xl mx-auto"
    >
      <div className="bg-zinc-950/95 backdrop-blur-md border border-zinc-800 rounded-sm shadow-2xl">
        {!customizing ? (
          <div className="p-5 md:p-6">
            <div className="flex items-start gap-3 mb-4">
              <Cookie className="w-5 h-5 text-[#FF4400] shrink-0 mt-0.5" />
              <div className="min-w-0">
                <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#FF4400] mb-2">
                  · Cookie & Privacy
                </div>
                <p className="text-sm text-zinc-300 font-body leading-relaxed">
                  Usiamo cookie tecnici essenziali per farti accedere a Reelio. Con il tuo consenso, attiviamo anche cookie analitici e di marketing per migliorare il servizio.{" "}
                  <Link to="/cookie-policy" className="text-[#FF4400] hover:text-[#FF6633] underline">
                    Maggiori info
                  </Link>
                  .
                </p>
              </div>
            </div>
            <div className="flex flex-col sm:flex-row gap-2 sm:gap-3">
              <button
                onClick={rejectAll}
                data-testid="cookie-banner-reject"
                className="px-4 py-2.5 bg-zinc-800 hover:bg-zinc-700 text-white font-display font-bold text-sm rounded-sm transition-colors"
              >
                Solo essenziali
              </button>
              <button
                onClick={() => setCustomizing(true)}
                data-testid="cookie-banner-customize"
                className="px-4 py-2.5 bg-zinc-900 border border-zinc-700 hover:border-zinc-500 text-white font-display font-bold text-sm rounded-sm transition-colors"
              >
                Personalizza
              </button>
              <button
                onClick={acceptAll}
                data-testid="cookie-banner-accept-all"
                className="px-5 py-2.5 bg-[#FF4400] hover:bg-[#FF6633] text-white font-display font-bold text-sm rounded-sm transition-colors flex-1 sm:flex-none"
              >
                Accetta tutto
              </button>
            </div>
          </div>
        ) : (
          <div className="p-5 md:p-6">
            <div className="flex items-start justify-between mb-4">
              <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#FF4400]">
                · Preferenze cookie
              </div>
              <button onClick={() => setCustomizing(false)} className="text-zinc-500 hover:text-white" aria-label="Chiudi">
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="space-y-4 mb-5">
              <div className="flex items-start justify-between gap-4 pb-3 border-b border-zinc-800">
                <div>
                  <div className="font-display font-bold text-sm text-white">Essenziali</div>
                  <div className="text-[12px] font-body text-zinc-400 mt-0.5">
                    Necessari per login, sessione, sicurezza. Non disattivabili.
                  </div>
                </div>
                <div className="text-[10px] font-mono uppercase tracking-widest text-green-400 shrink-0 pt-1">
                  Sempre attivi
                </div>
              </div>
              <label className="flex items-start justify-between gap-4 pb-3 border-b border-zinc-800 cursor-pointer">
                <div>
                  <div className="font-display font-bold text-sm text-white">Analitici</div>
                  <div className="text-[12px] font-body text-zinc-400 mt-0.5">
                    PostHog. Statistiche aggregate di utilizzo per migliorare il prodotto.
                  </div>
                </div>
                <input
                  type="checkbox"
                  checked={analytics}
                  onChange={(e) => setAnalytics(e.target.checked)}
                  data-testid="cookie-banner-toggle-analytics"
                  className="mt-1 w-5 h-5 accent-[#FF4400]"
                />
              </label>
              <label className="flex items-start justify-between gap-4 cursor-pointer">
                <div>
                  <div className="font-display font-bold text-sm text-white">Marketing</div>
                  <div className="text-[12px] font-body text-zinc-400 mt-0.5">
                    Nessun cookie di marketing attivo al momento. Riservato per future campagne.
                  </div>
                </div>
                <input
                  type="checkbox"
                  checked={marketing}
                  onChange={(e) => setMarketing(e.target.checked)}
                  data-testid="cookie-banner-toggle-marketing"
                  className="mt-1 w-5 h-5 accent-[#FF4400]"
                />
              </label>
            </div>
            <button
              onClick={saveCustom}
              data-testid="cookie-banner-save"
              className="w-full px-5 py-2.5 bg-[#FF4400] hover:bg-[#FF6633] text-white font-display font-bold text-sm rounded-sm transition-colors"
            >
              Salva preferenze
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
